import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";

import {
  buyForOrder,
  changeOrder,
  getBillCreated,
  getOrderNumberUuid,
} from "../../store/order";
import OrderStatusChecker from "./orderStatusChecker";

const EMAIL_SCHEMA = yup.object().shape({
  email: yup
    .string()
    .matches(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      "Введите корректную почту"
    ),
});

function ShopCartCheckoutForm({ cart, isVisible = true }) {
  const dispatch = useDispatch();
  const orderNumberUuid = useSelector(getOrderNumberUuid());
  const billCreated = useSelector(getBillCreated());

  const [data, setData] = useState("");
  const [emailChanged, setEmailChanged] = useState(false);
  const [error, setError] = useState(null);

  const showWarning = useMemo(
    () => !cart.userEmail || emailChanged || cart.finalSum < 1,
    [cart.userEmail, cart.finalSum, emailChanged]
  );
  const warningMessage = useMemo(() => {
    if (!cart.userEmail || emailChanged) {
      return "Перед покупкой укажите свой email. На него будут отправлены счёт и коды после оплаты.";
    }
    if (cart.finalSum < 1) return "Выберите товар для покупки.";
    return "";
  }, [cart.userEmail, cart.finalSum, emailChanged]);

  useEffect(() => {
    setData(cart.userEmail);
    setEmailChanged(false);
    setError(null);
  }, [cart]);

  if (!isVisible) return null;

  const handleChange = (e) => {
    const nextValue = e.target.value;

    if (nextValue !== "") {
      EMAIL_SCHEMA.validate({ email: nextValue })
        .then(() => setError(null))
        .catch((r) => setError(r.message));
    } else {
      setError("Введите почту");
    }

    setEmailChanged(true);
    setData(nextValue);
  };

  const handleEmail = () => {
    dispatch(
      changeOrder({
        orderNumberUuid,
        email: data,
      })
    );
    setEmailChanged(false);
  };

  const handleBuy = (e) => {
    e.preventDefault();
    window.ym(93997310, "reachGoal", "clickBuyEshop");
    dispatch(buyForOrder({ orderNumberUuid }));
  };

  const isBuyDisabled =
    !cart.userEmail || emailChanged || cart.finalSum < 1 || billCreated;

  return (
    <>
      <form
        className=" flex w-full flex-col items-center gap-0"
        onSubmit={handleBuy}
      >
        <span
          className={
            " mb-2 w-full overflow-hidden text-xs text-red-600 transition-all duration-300 " +
            (error ? " opacity-1 " : "  invisible opacity-0  ")
          }
        >
          {error}
        </span>

        <input
          type="text"
          className={
            "mx-2 mb-2 w-full rounded-md border border-gray-400 bg-white px-3 py-1.5 text-black placeholder-gray-600 focus:border-blue-600 focus:ring-1 focus:ring-blue-600 dark:border-gray-600 dark:bg-gray-900 dark:text-white dark:placeholder-gray-400" +
            (billCreated
              ? " cursor-not-allowed bg-gray-100 text-gray-500 placeholder-gray-400 focus:border-gray-400 focus:ring-0 dark:bg-gray-800 dark:text-gray-500 dark:placeholder-gray-600"
              : "")
          }
          placeholder="Ваш email"
          value={data}
          onChange={handleChange}
          disabled={billCreated}
        />

        <button
          className="shop-action-button mx-2 mb-2 w-full"
          type="button"
          onClick={handleEmail}
          disabled={!!error || !emailChanged || billCreated}
        >
          Загрузить персональную скидку
        </button>

        {showWarning && (
          <span className=" mb-2 w-full overflow-hidden text-xs text-red-600 transition-all duration-300 ">
            {warningMessage}
          </span>
        )}

        <button
          className={
            "mx-2 mt-2 w-full " +
            (isBuyDisabled
              ? "shop-action-button shop-action-button-muted"
              : "shop-action-button shop-action-button-green")
          }
          disabled={isBuyDisabled}
        >
          Купить
        </button>

        <div className="mb-3 w-full text-center">
          <div className="text-sm text-red-600">
            ❗️🚨<b>Проверяйте папку СПАМ</b>🚨❗️
          </div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            или вернитесь сюда для получения кодов
          </div>
        </div>
      </form>

      {billCreated && (
        <OrderStatusChecker
          orderNumberUuid={orderNumberUuid}
          userEmail={cart.userEmail}
        />
      )}
    </>
  );
}

export default ShopCartCheckoutForm;
